import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ApplicantProfile} from '../types';

import {applicantProfileOverride} from './applicantProfileOverride';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {useApiRead} from '../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';
import {Patch} from '../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../lib/api/PATCH/PatchRoutes';
import {ApiResponseError} from '../../../../lib/api/v1/types';
import {ApplicantRoleData} from '../../../../types/User';

export const useApplicantProfile = () => {
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();
  const lang = useContext(LanguageContext);
  const {currentUser} = useContext(UserContext);
  const applicantId = (currentUser?.role.data as ApplicantRoleData).applicantId;

  const [currentProfile, setCurrentProfile] = useState<ApplicantProfile>();

  const {data, isLoading, error, refetch} = useApiRead<ApplicantProfile>({
    path: GetRoutes.getApplicantProfile(applicantId),
    options: {enabled: !!applicantId},
    camelCaseOverride: applicantProfileOverride.camelCase(lang),
  });

  const onSuccess = (updated: ApplicantProfile) => {
    setCurrentProfile(updated);
    dispatchMessage(t('shared.formSubmit.success'), 'success');
  };

  const onError = (error?: ApiResponseError) => {
    if (error) {
      dispatchMessage(t('shared.formSubmit.error', error), 'error');
    }
  };

  const update = useApiWrite<Patch.UpdateApplicantProfile, ApplicantProfile>({
    path: PatchRoutes[Patch.UpdateApplicantProfile](applicantId),
    method: 'patch',
    snakeCaseOverride: applicantProfileOverride.snakeCase,
    camelCaseOverride: applicantProfileOverride.camelCase(lang),
    onSuccess,
    onError,
  });

  const updateProfile = (values: Partial<ApplicantProfile>) => {
    // Build the request from the freshest profile we have, not the (potentially
    // stale) initial react-query payload.
    const base = (currentProfile ?? data) as ApplicantProfile;
    const transformedData = applicantProfileOverride.transformDataForUpdate(base, values);
    update.makeRequest(transformedData);
  };

  useEffect(() => {
    setCurrentProfile(data);
  }, [data]);

  return {
    profile: currentProfile,
    isLoading: isLoading || update.isLoading,
    error: error || update.error,
    updateProfile,
    refreshProfile: refetch,
  };
};
